import React, { useState, useEffect, useCallback } from 'react';
import PageRenderer from './PageRenderer';
import UserProfile from '../UserProfile';
import LessonSidebar from './LessonSidebar';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const UnifiedLessonViewer = ({ onComplete }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const courseId = searchParams.get('courseId');
  const moduleId = searchParams.get('moduleId');
  const lessonId = searchParams.get('lessonId');
  
  const [lesson, setLesson] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResults, setQuizResults] = useState({});
  const [quizAnswerResults, setQuizAnswerResults] = useState({});
  const [isCompleted, setIsCompleted] = useState(false);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [checkpointStatus, setCheckpointStatus] = useState({});
  const [virtualClassCompleted, setVirtualClassCompleted] = useState(false);
  const [virtualClassSeeCompleted, setVirtualClassSeeCompleted] = useState(false);
  const [virtualClassHearCompleted, setVirtualClassHearCompleted] = useState(false);
  const [virtualClassDoCompleted, setVirtualClassDoCompleted] = useState(false);
  const [arcQuizCompleted, setArcQuizCompleted] = useState(false);
  const [wasEverCompleted, setWasEverCompleted] = useState(false);
  const [triggerQuizFeedback, setTriggerQuizFeedback] = useState(false); 

  useEffect(() => {
    const loadLesson = async () => {
      if (!lessonId) {
        setError('Lesson ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await api.get(`/lessons/${lessonId}`);
        
        if (response.data.success) {
          const responseData = response.data.data;
          
          const lessonStructuredData = responseData;
          
          if (!lessonStructuredData.profileImage && lessonStructuredData.profileImage === undefined) {
            console.log('ProfileImage not found at root, checking lessonData structure:', Object.keys(lessonStructuredData));
          }
          
          
          if (!lessonStructuredData.pages || !Array.isArray(lessonStructuredData.pages) || lessonStructuredData.pages.length === 0) {
            console.error('Lesson has no pages:', {
              lessonId: lessonStructuredData.id,
              title: lessonStructuredData.title,
              hasPages: !!lessonStructuredData.pages,
              pagesType: typeof lessonStructuredData.pages,
              responseDataKeys: Object.keys(responseData)
            });
            setError('Lesson has no pages. Please contact support.');
            return;
          }
          
          setLesson(lessonStructuredData);
          
          const progress = responseData.progress;
          const isLessonCompleted = progress?.status === 'COMPLETED';
          
          if (isLessonCompleted) {
            setWasEverCompleted(true);
          }
          
          const savedPageIndex = isLessonCompleted ? 0 : (progress?.currentPageIndex);
          let validPageIndex = 0;
          
          if (savedPageIndex !== undefined && savedPageIndex !== null) {
            if (savedPageIndex >= 0 && savedPageIndex < lessonStructuredData.pages.length) {
              validPageIndex = savedPageIndex;
            } else {
              validPageIndex = 0;
            }
          }
          
          const checkpointStatusData = responseData.checkpointStatus || {};
          setCheckpointStatus(checkpointStatusData);
          
          const initialQuizResults = {};
          Object.keys(checkpointStatusData).forEach(checkpointId => {
            const status = checkpointStatusData[checkpointId];
            if (status && status.score !== undefined) {
              initialQuizResults[checkpointId] = {
                score: status.score,
                passed: status.passed || false,
                correct: status.correctCount || 0,
                total: status.totalQuestions || 0,
              };
            }
          });
          setQuizResults(initialQuizResults);
          
          setQuizAnswers({});
          setQuizAnswerResults({});
          
          
          let adjustedPageIndex = validPageIndex;
          const savedPage = lessonStructuredData.pages[validPageIndex];
          
          if (savedPage && savedPage.type === 'checkpoint-directions') {
            const checkpointId = savedPage.checkpointId;
            if (checkpointId) {
              const status = checkpointStatusData[checkpointId];
              if (status && (status.passed === true || status.canAttempt === false)) {
                adjustedPageIndex = skipCheckpointPages(lessonStructuredData.pages, validPageIndex, checkpointStatusData);
                if (adjustedPageIndex !== validPageIndex) {
                }
              }
            }
          }
          
          // Resume where they left off. cover (0).
          if (isLessonCompleted || savedPageIndex === undefined || savedPageIndex === null) {
            setCurrentPageIndex(0);
          } else {
            setCurrentPageIndex(
              Math.min(adjustedPageIndex + 1, lessonStructuredData.pages.length)
            );
          }

        } else {
          setError(response.data.message || 'Failed to load lesson');
        }
      } catch (err) {
        
        setError(err.response?.data?.message || 'Failed to load lesson. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
  }, [lessonId]);

 
  useEffect(() => {
    if (lesson && lesson.pages) {
      const actualPageIndex = currentPageIndex === 0 ? null : currentPageIndex - 1;
      const page = actualPageIndex !== null ? lesson.pages[actualPageIndex] : null;
      
      
      setVirtualClassCompleted(false);
      setVirtualClassSeeCompleted(false);
      setVirtualClassHearCompleted(false);
      setVirtualClassDoCompleted(false);
      setArcQuizCompleted(false);
    }
  }, [currentPageIndex, lesson]);

  const saveProgress = useCallback(async (pageIndex, status) => {
    if (!lessonId) return;

    try {
      const finalStatus = wasEverCompleted ? 'COMPLETED' : status;
      
      await api.post(`/lessons/${lessonId}/progress`, {
        currentPageIndex: pageIndex,
        status: finalStatus,
      });
    } catch (err) {
      
    }
  }, [lessonId, wasEverCompleted]);

  const saveCheckpointAnswer = useCallback(async (pageId, checkpointId, answer) => {
    if (!lessonId) return;

    try {
      const response = await api.post(`/lessons/${lessonId}/checkpoint-answers`, {
        checkpointId,
        pageId,
        answer,
      });
      
      
      if (response.data.success && response.data.data?.checkpointAnswer) {
        const isCorrect = response.data.data.checkpointAnswer.isCorrect;
        setQuizAnswerResults(prev => ({
          ...prev,
          [checkpointId]: {
            ...prev[checkpointId],
            [pageId]: isCorrect
          }
        }));
      }
    } catch (err) {
      
    }
  }, [lessonId]);

  const submitCheckpoint = useCallback(async (checkpointId) => {
    if (!lessonId) return null;

    try {
      const response = await api.post(`/lessons/${lessonId}/checkpoint-submit`, {
        checkpointId,
      });
      
      if (response.data.success && response.data.data) {
        const { score, passed, correctCount, totalQuestions, canAttempt, attemptsRemaining } = response.data.data;
        
        setCheckpointStatus(prev => ({
          ...prev,
          [checkpointId]: {
            ...prev[checkpointId],
            passed,
            canAttempt,
            attemptsRemaining,
            score,
            correctCount,
            totalQuestions,
          }
        }));

        setQuizResults(prev => ({
          ...prev,
          [checkpointId]: {
            score,
            passed,
            correct: correctCount,
            total: totalQuestions,
          }
        }));

        return response.data.data;
      }
      return null;
    } catch (err) {
      
      const errorMessage = err.response?.data?.message || err.message || 'Error submitting checkpoint';
      alert(errorMessage);
      return null;
    }
  }, [lessonId]);

  const handleNext = async () => {
    if (!lesson || !lesson.pages) return;
    
    const actualPageIndex = currentPageIndex === 0 ? null : currentPageIndex - 1;
    const currentPage = actualPageIndex !== null ? lesson.pages[actualPageIndex] : null;
    
    if (currentPage && currentPage.type === 'quiz-question') {
      const checkpointId = currentPage.checkpointId;
      const pageId = currentPage.id;
      const answer = quizAnswers[checkpointId]?.[pageId];
      
      
      if (answer !== undefined && answer !== null && answer !== '' && !triggerQuizFeedback) {
        setTriggerQuizFeedback(true);
        return; 
      }

      if (triggerQuizFeedback) {
        setTriggerQuizFeedback(false);
 
      }
    }
    
   
    if (currentPage && currentPage.type === 'quiz-question') {
      const checkpointId = currentPage.checkpointId;
      const checkpointPages = lesson.pages.filter(
        p => p.checkpointId === checkpointId && p.type === 'quiz-question'
      );
      const currentQuestionIndex = checkpointPages.findIndex(p => p.id === currentPage.id);
      const isLastQuestion = currentQuestionIndex === checkpointPages.length - 1;
      
      if (isLastQuestion && checkpointId) {
        const result = await submitCheckpoint(checkpointId);
        
        if (result && result.passed) {
          const resultsPageIndex = lesson.pages.findIndex(
            p => p.type === 'quiz-results' && p.checkpointId === checkpointId
          );
          
          if (resultsPageIndex !== -1) {
            const resultsDisplayIndex = resultsPageIndex + 1;
            setCurrentPageIndex(resultsDisplayIndex);
            
            saveProgress(resultsPageIndex, wasEverCompleted ? 'COMPLETED' : 'IN_PROGRESS');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return; 
          }
        } else {
          const resultsPageIndex = lesson.pages.findIndex(
            p => p.type === 'quiz-results' && p.checkpointId === checkpointId
          );
          
          if (resultsPageIndex !== -1) {
            const resultsDisplayIndex = resultsPageIndex + 1;
            setCurrentPageIndex(resultsDisplayIndex);
            
            saveProgress(resultsPageIndex, wasEverCompleted ? 'COMPLETED' : 'IN_PROGRESS');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return; 
          }
        }
      }
    }
    
    const totalPagesWithCover = lesson.pages.length + 1;
    
    if (currentPageIndex < totalPagesWithCover - 1) {
      let newPageIndex = currentPageIndex + 1;
      
      if (newPageIndex > 0) {
        const actualNewPageIndex = newPageIndex - 1;
        const nextPage = lesson.pages[actualNewPageIndex];
        const currentActualPage = actualPageIndex !== null ? lesson.pages[actualPageIndex] : null;
        
        if (nextPage && (nextPage.type === 'checkpoint-directions' || nextPage.type === 'quiz-question')) {
          const checkpointId = nextPage.checkpointId || (nextPage.type === 'quiz-question' ? nextPage.checkpointId : null);
          if (checkpointId && currentActualPage && 
              currentActualPage.type !== 'checkpoint-directions' && 
              currentActualPage.type !== 'quiz-question' && 
              currentActualPage.type !== 'quiz-results') {
            setQuizAnswers(prev => {
              const newAnswers = { ...prev };
              if (newAnswers[checkpointId]) {
                delete newAnswers[checkpointId];
              }
              return newAnswers;
            });
            setQuizAnswerResults(prev => {
              const newResults = { ...prev };
              if (newResults[checkpointId]) {
                delete newResults[checkpointId];
              }
              return newResults;
            });
          }
        }
        
        if (nextPage && (nextPage.type === 'quiz-results' || nextPage.type === 'quiz-question')) {
        } else {
          const skippedActualIndex = skipCheckpointPages(lesson.pages, actualNewPageIndex, checkpointStatus);
          newPageIndex = skippedActualIndex + 1; 
        }
      }
      
      setCurrentPageIndex(newPageIndex);
      
      const isLastPage = newPageIndex >= totalPagesWithCover - 1;
      
      const savePageIndex = newPageIndex > 0 ? newPageIndex - 1 : 0;
      saveProgress(savePageIndex, wasEverCompleted ? 'COMPLETED' : 'IN_PROGRESS');
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setIsCompleted(true);
      setShowCompletionScreen(true);
      setWasEverCompleted(true); 
      saveProgress(lesson.pages.length - 1, 'COMPLETED');
      onComplete?.();
    }
  };

  const findNextNonCheckpointPage = (pages, startIndex) => {
    for (let i = startIndex; i < pages.length; i++) {
      const page = pages[i];
      if (page.type === 'quiz-results' || page.type === 'quiz-question') {
        return i;
      }
      if (page.type !== 'checkpoint-directions') {
        return i;
      }
    }
    return pages.length - 1; 
  };

  const skipCheckpointPages = (pages, startIndex, checkpointStatus) => {
    let currentIndex = startIndex;
    
    if (currentIndex < pages.length) {
      const startPage = pages[currentIndex];
      if (startPage.type === 'quiz-question' || startPage.type === 'quiz-results') {
        return currentIndex; 
      }
    }
    
    while (currentIndex < pages.length) {
      const page = pages[currentIndex];
      
      if (page.type === 'quiz-question' || page.type === 'quiz-results') {
        break; 
      }
      
      if (page.type === 'checkpoint-directions') {
        const checkpointId = page.checkpointId;
        if (checkpointId) {
          const status = checkpointStatus[checkpointId];
          
          if (status && (status.passed === true || status.canAttempt === false)) {
            let foundQuestion = false;
            for (let i = currentIndex + 1; i < pages.length; i++) {
              const nextPage = pages[i];
              if (nextPage.type === 'quiz-question' && nextPage.checkpointId === checkpointId) {
                currentIndex = i;
                foundQuestion = true;
                break;
              }
              if (nextPage.type !== 'checkpoint-directions' && 
                  nextPage.type !== 'quiz-question' && 
                  nextPage.type !== 'quiz-results') {
                currentIndex = i;
                break;
              }
              if (nextPage.type === 'quiz-results' && nextPage.checkpointId === checkpointId) {
                currentIndex = i;
                break;
              }
            }
            if (foundQuestion) {
              break; 
            }
            break;
          }
        }
      }
      
      break;
    }
    
    return currentIndex;
  };

  const findPreviousNonCheckpointPage = (pages, startIndex, checkpointStatus) => {
    for (let i = startIndex; i >= 0; i--) {
      const page = pages[i];
      
      if (page.type === 'quiz-results') {
        return i;
      }
      
      if (page.type === 'checkpoint-directions' || page.type === 'quiz-question') {
        const checkpointId = page.checkpointId;
        if (checkpointId) {
          const status = checkpointStatus[checkpointId];
          if (status && (status.passed === true || status.canAttempt === false)) {
            continue; 
          }
        }
      }
      
      return i;
    }
    return 0; 
  };

  const handlePrevious = () => {
    if (currentPageIndex > 0) {
      let newPageIndex = currentPageIndex - 1;
      
      if (newPageIndex > 0) {
        const actualPageIndex = newPageIndex - 1;
        const currentActualPage = lesson.pages[actualPageIndex];
        
        if (currentActualPage && currentActualPage.type === 'quiz-results') {
          const checkpointId = currentActualPage.checkpointId;
          if (checkpointId) {
            const checkpointDirectionsPage = lesson.pages.find(
              p => p.checkpointId === checkpointId && p.type === 'checkpoint-directions'
            );
            
            if (checkpointDirectionsPage) {
              const directionsIndex = lesson.pages.findIndex(p => p.id === checkpointDirectionsPage.id);
              if (directionsIndex > 0) {
                const pageBeforeDirections = lesson.pages[directionsIndex - 1];
                const pageBeforeIndex = lesson.pages.findIndex(p => p.id === pageBeforeDirections.id);
                if (pageBeforeIndex !== -1) {
                  newPageIndex = pageBeforeIndex + 1; 
                }
              } else {
                newPageIndex = 0;
              }
            }
          }
        } else {
          const skippedActualIndex = findPreviousNonCheckpointPage(
            lesson.pages, 
            actualPageIndex, 
            checkpointStatus
          );
          newPageIndex = skippedActualIndex + 1; // Add 1 back for title/image offset
        }
      }
      
      setCurrentPageIndex(newPageIndex);
      const savePageIndex = newPageIndex > 0 ? newPageIndex - 1 : 0;
      saveProgress(savePageIndex, wasEverCompleted ? 'COMPLETED' : 'IN_PROGRESS');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleAnswer = (pageId, checkpointId, answer) => {
    setQuizAnswers(prev => ({
      ...prev,
      [checkpointId]: {
        ...prev[checkpointId],
        [pageId]: answer
      }
    }));

    saveCheckpointAnswer(pageId, checkpointId, answer);
  };

  const handleRetakeCheckpoint = (checkpointId) => {
    if (!lesson || !lesson.pages) return;
    
    setQuizAnswers(prev => {
      const newAnswers = { ...prev };
      if (newAnswers[checkpointId]) {
        delete newAnswers[checkpointId];
      }
      return newAnswers;
    });
    
    setQuizResults(prev => {
      const newResults = { ...prev };
      if (newResults[checkpointId]) {
        delete newResults[checkpointId];
      }
      return newResults;
    });
    
    setCheckpointStatus(prev => ({
      ...prev,
      [checkpointId]: {
        ...prev[checkpointId],
        passed: undefined, 
        canAttempt: true
      }
    }));
    
    const directionsPageIndex = lesson.pages.findIndex(
      p => p.type === 'checkpoint-directions' && p.checkpointId === checkpointId
    );
    
    if (directionsPageIndex !== -1) {
      const displayPageIndex = directionsPageIndex + 1;
      setCurrentPageIndex(displayPageIndex);
      saveProgress(directionsPageIndex, wasEverCompleted ? 'COMPLETED' : 'IN_PROGRESS');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const canNavigateNext = (page) => {
    if (currentPageIndex === 0) {
      return true;
    }
    
    if (!page || !page.type) return false;
    
    if (page.type === 'quiz-question') {
      const checkpointId = page.checkpointId;
      const pageId = page.id;
      
      const answer = quizAnswers[checkpointId]?.[pageId];
      const hasAnswer = answer !== undefined && answer !== null && answer !== '';
      
      // If feedback was shown, allow navigation even if answer check passes
      // (feedback being shown means answer exists)
      if (triggerQuizFeedback) {
        return true;
      }
      
      return hasAnswer;
    }
    
    if (page.type === 'quiz-results') {
      const checkpointId = page.checkpointId;
      const results = quizResults[checkpointId];
      
      if (results && results.passed === false) {
        return false;
      }
      
      return true;
    }
    
    return true;
  };

  const isCheckpointPage = (page) => {
    if (!page || !page.type) return false;
    return page.type === 'checkpoint-directions' || 
           page.type === 'quiz-question' || 
           page.type === 'quiz-results';
  };

  useEffect(() => {
    if (!lesson || !lesson.pages || Object.keys(checkpointStatus).length === 0) {
      return;
    }
    
    if (currentPageIndex === 0) {
      return;
    }
    
    const actualPageIndex = currentPageIndex - 1;
    const currentPageCheck = lesson.pages[actualPageIndex];
    
    if (currentPageCheck && currentPageCheck.type === 'checkpoint-directions') {
      const checkpointId = currentPageCheck.checkpointId;
      if (checkpointId) {
        const status = checkpointStatus[checkpointId];
        if (status && (status.passed === true || status.canAttempt === false)) {
          const skippedActualIndex = skipCheckpointPages(lesson.pages, actualPageIndex, checkpointStatus);
          const skippedIndex = skippedActualIndex + 1; 
          if (skippedIndex !== currentPageIndex) {
            setCurrentPageIndex(skippedIndex);
          }
        } else {
        }
      }
    }
  }, [currentPageIndex, lesson, checkpointStatus]);

  useEffect(() => {
    if (!lesson || !lesson.pages || currentPageIndex === 0) {
      return;
    }

    const actualPageIndex = currentPageIndex - 1;
    const currentPage = lesson.pages[actualPageIndex];
    
    if (currentPage && currentPage.type === 'quiz-results' && currentPage.checkpointId) {
      const checkpointId = currentPage.checkpointId;
      
      if (!quizResults[checkpointId]) {
        submitCheckpoint(checkpointId).catch(err => {
        });
      }
    }
  }, [currentPageIndex, lesson, quizResults, submitCheckpoint]);

  const calculateProgress = () => {
    if (!lesson) return 0;
    const totalPagesWithCover = lesson.pages.length + 1;
    
    if (currentPageIndex >= totalPagesWithCover - 1 && !isCompleted) {
      return 99;
    }
    return Math.round(((currentPageIndex + 1) / totalPagesWithCover) * 100);
  };

  const calculateProgressCircle = () => {
    const progress = calculateProgress();
    const circumference = 2 * Math.PI * 20; 
    const offset = circumference - (progress / 100) * circumference;
    return { circumference, offset };
  };

  if (loading) {
    return (
      <div className="fixed inset-0 w-full h-full bg-white overflow-auto z-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading lesson...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="fixed inset-0 w-full h-full bg-white overflow-auto z-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-500 text-lg mb-4">{error || 'Lesson not found'}</p>
            <Link to={`/dashboard/modules${courseId ? `?courseId=${courseId}` : ''}`}>
              <button className="px-6 py-2 bg-primary text-white rounded hover:bg-blue-700">
                Back to Modules
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!lesson || !lesson.pages || !Array.isArray(lesson.pages) || lesson.pages.length === 0) {
    return (
      <div className="fixed inset-0 w-full h-full bg-white overflow-auto z-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-500 text-lg mb-4">Lesson data is invalid or empty</p>
            <Link to={`/dashboard/modules${courseId ? `?courseId=${courseId}` : ''}`}>
              <button className="px-6 py-2 bg-primary text-white rounded hover:bg-blue-700">
                Back to Modules
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const actualPageIndex = currentPageIndex === 0 ? null : currentPageIndex - 1;
  const currentPage = actualPageIndex !== null ? lesson.pages[actualPageIndex] : null;
  const totalPages = lesson.pages.length + 1; 
  
  if (currentPageIndex > 0 && !currentPage) {
    return (
      <div className="fixed inset-0 w-full h-full bg-white overflow-auto z-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-500 text-lg mb-4">Page not found</p>
            <Link to={`/dashboard/modules${courseId ? `?courseId=${courseId}` : ''}`}>
              <button className="px-6 py-2 bg-primary text-white rounded hover:bg-blue-700">
                Back to Modules
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  const progress = calculateProgress();
  const { offset } = calculateProgressCircle();

  if (showCompletionScreen) {
    return (
      <div className="fixed inset-0 w-full h-full bg-gradient-to-br from-blue-50 to-green-50 overflow-auto z-50">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12 max-w-2xl w-full text-center">
            <div className="mb-6">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-2">Lesson Completed!</h2>
              <p className="text-lg text-gray-600">Congratulations! You have successfully completed this lesson.</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link to={`/dashboard/modules${courseId ? `?courseId=${courseId}` : ''}`}>
                <button className="px-8 py-4 bg-primary hover:bg-blue-700 text-white rounded-full font-semibold text-lg transition-colors shadow-lg hover:shadow-xl">
                  Back to Modules
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 w-full h-full bg-white overflow-hidden z-50">
      <div className="flex flex-col h-full w-full">
        <header className="fixed top-0 left-0 right-0 bg-primary text-white px-4 lg:px-6 py-2.5 flex items-center justify-between z-50 shadow-md">
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12">
              <svg className="w-12 h-12 transform -rotate-90">
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="4"
                  fill="none"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  stroke="white"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray="126"
                  strokeDashoffset={offset}
                  strokeLinecap="round"
                  className="transition-all duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-xs font-semibold">
                <span className="text-[10px] leading-tight">{progress}%</span>
                <span className="text-[8px] font-normal leading-tight">Done</span>
              </div>
            </div>
            <div>
              <h1 className="text-sm lg:text-base font-semibold leading-tight">{lesson.title}</h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigate('/dashboard')}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-1.5 rounded-lg transition-colors font-medium text-sm"
            >
              Dashboard
            </button>
            <button 
              onClick={() => navigate('/dashboard/settings')}
              className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
              title="Settings"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <UserProfile showRole={false} />
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-[#E2F2FF] pt-16 pb-20">
          <div className="flex max-w-[1600px] mx-auto">
            <div className="flex-1 px-4 lg:px-8 py-6 lg:py-8">
              {currentPageIndex === 0 && lesson ? (
                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div className="px-6 lg:px-8 pt-6 pb-4">
                    <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                      {lesson.title}
                    </h1>
                  </div>
                  
                  {lesson.profileImage ? (
                    <div className="w-full flex items-center justify-center bg-gray-50" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
                      <img
                        src={lesson.profileImage}
                        alt={lesson.title}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => {
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-full flex items-center justify-center text-gray-400" style={{ height: 'calc(100vh - 280px)', minHeight: '500px' }}>
                      <p>No image available</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-md p-6 lg:p-8">
                  <PageRenderer
                    page={currentPage}
                    lesson={lesson ? { ...lesson, databaseId: lessonId } : null}
                    quizAnswers={quizAnswers}
                    quizResults={quizResults}
                    quizAnswerResults={quizAnswerResults}
                    checkpointStatus={checkpointStatus}
                    onAnswer={handleAnswer}
                    onNext={handleNext}
                    onRetake={handleRetakeCheckpoint}
                    onCompletionChange={(completed) => {
                      if (currentPage.type === 'virtual-class') {
                        setVirtualClassCompleted(completed);
                      } else if (currentPage.type === 'virtual-class-see') {
                        setVirtualClassSeeCompleted(completed);
                      } else if (currentPage.type === 'virtual-class-hear') {
                        setVirtualClassHearCompleted(completed);
                      } else if (currentPage.type === 'virtual-class-do') {
                        setVirtualClassDoCompleted(completed);
                      } else if (currentPage.type === 'arc-quiz') {
                        setArcQuizCompleted(completed);
                      }
                    }}
                    triggerQuizFeedback={triggerQuizFeedback}
                    onFeedbackComplete={() => setTriggerQuizFeedback(false)}
                  />
                </div>
              )}
            </div>

            {moduleId && courseId && currentPageIndex === 0 && (
              <LessonSidebar
                moduleId={moduleId}
                currentLessonId={lessonId}
                courseId={courseId}
              />
            )}
          </div>
        </main>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-2.5">
            <div className="flex justify-between items-center gap-3">
              <Link to={`/dashboard/modules${courseId ? `?courseId=${courseId}` : ''}`}>
                <button className="px-6 py-2.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm">
                  ← Back to Modules
                </button>
              </Link>

              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrevious}
                  disabled={currentPageIndex === 0 || (currentPage && (currentPage.type === 'quiz-results' || currentPage.type === 'quiz-question'))}
                  className={`px-6 py-2.5 rounded-full font-medium transition-colors ${
                    currentPageIndex === 0 || (currentPage && (currentPage.type === 'quiz-results' || currentPage.type === 'quiz-question'))
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-[#E2F2FF] hover:bg-[#c6e1f7] text-gray-700'
                  }`}
                >
                  ← Previous
                </button>
                <button
                  onClick={handleNext}
                  disabled={(currentPageIndex === 0 ? false : (!canNavigateNext(currentPage) || 
                    (currentPage && currentPage.type === 'virtual-class' && !virtualClassCompleted) ||
                    (currentPage && currentPage.type === 'virtual-class-see' && !virtualClassSeeCompleted) ||
                    (currentPage && currentPage.type === 'virtual-class-hear' && !virtualClassHearCompleted) ||
                    (currentPage && currentPage.type === 'virtual-class-do' && !virtualClassDoCompleted) ||
                    (currentPage && currentPage.type === 'arc-quiz' && !arcQuizCompleted)))}
                  className={`px-6 py-2.5 rounded-full font-medium transition-colors ${
                    (currentPageIndex === 0 ? false : (!canNavigateNext(currentPage) || 
                      (currentPage && currentPage.type === 'virtual-class' && !virtualClassCompleted) ||
                      (currentPage && currentPage.type === 'virtual-class-see' && !virtualClassSeeCompleted) ||
                      (currentPage && currentPage.type === 'virtual-class-hear' && !virtualClassHearCompleted) ||
                      (currentPage && currentPage.type === 'virtual-class-do' && !virtualClassDoCompleted) ||
                      (currentPage && currentPage.type === 'arc-quiz' && !arcQuizCompleted)))
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-primary hover:bg-blue-700 text-white'
                  }`}
                >
                  {currentPageIndex >= totalPages - 1 ? 'Complete' : 'Next →'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedLessonViewer;
